import React, { Dispatch, SetStateAction } from 'react';
import { MapContainer, GeoJSON, useMapEvents } from 'react-leaflet';
import { Feature } from 'geojson';
import { LatLngExpression, Layer } from 'leaflet';
import { getCountryNameByLang, getCountryById, loadMapData, getCountryByName } from '../../utils/utils';
import { STATE_DEFAULT, STATE_ACTIVE, STATE_INACTIVE } from '../../utils/variables';
import { Country, CountryStyle } from '../../types/Country';
import "leaflet/dist/leaflet.css";

const mapData = loadMapData();

type Props = {
  state: Array<Country>;
  selectCountry: any;
  selectedCountry: Country | null;
  changed: boolean;
  zoom: number;
  setZoom: (zoom: number) => void;
  center: LatLngExpression;
  setCenter: Dispatch<SetStateAction<LatLngExpression>>;
}

const Map: React.FunctionComponent<Props> = (props: Props) => {
  const { state, selectCountry, selectedCountry, changed, zoom, setZoom, setCenter } = props;

  const onSelect = (event: any, country: Country, layer:any) => {
    if (country === selectedCountry) {
      selectCountry('');
    }
    else {
      selectCountry(country);
    }
  }

  // Hover
  //const onHover = (event: { target: { setStyle: (arg0: { color: string; fillColor: string; weight: number; fillOpacity: number; }) => void } }) => {
  const onHover = (event: any) => {
    const countryId = String(event.target.feature.id);
    const matchingCountry = getCountryById(state, countryId);
    if (matchingCountry) {
      event.target.setStyle({
        fillColor: matchingCountry.activeStyle.fillColor,
        color: matchingCountry.activeStyle.color,
        weight: matchingCountry.activeStyle.weight,
        fillOpacity: matchingCountry.activeStyle.fillOpacity,
      });
    }
  };

  // Unhover
  //const onUnhover = (event: { target: { setStyle: (arg0: { color: string; fillColor: string; weight: number; fillOpacity: number; }) => void; }; }) => {
  const onUnhover = (event: any) => {
    const countryId = String(event.target.feature.id);
    const matchingCountry = getCountryById(state, countryId);
    if (matchingCountry) {
      switch (matchingCountry.state) {
        case STATE_DEFAULT:
          event.target.setStyle({
            fillColor: matchingCountry.defaultStyle.fillColor,
            color: matchingCountry.defaultStyle.color,
            weight: matchingCountry.defaultStyle.weight,
            fillOpacity: matchingCountry.defaultStyle.fillOpacity,
          });
          break;
        case STATE_ACTIVE:
          event.target.setStyle({
            fillColor: matchingCountry.activeStyle.fillColor,
            color: matchingCountry.activeStyle.color,
            weight: matchingCountry.activeStyle.weight,
            fillOpacity: matchingCountry.activeStyle.fillOpacity,
          }); 
          break;
        case STATE_INACTIVE:
          event.target.setStyle({
            fillColor: matchingCountry.inactiveStyle.fillColor,
            color: matchingCountry.inactiveStyle.color,
            weight: matchingCountry.inactiveStyle.weight,
            fillOpacity: matchingCountry.inactiveStyle.fillOpacity,
          }); 
          break;
        default:
          event.target.setStyle({
            fillColor: matchingCountry.inactiveStyle.fillColor,
            color: matchingCountry.inactiveStyle.color,
            weight: matchingCountry.inactiveStyle.weight,
            fillOpacity: matchingCountry.inactiveStyle.fillOpacity,
          });
          break;
        }
    }
  };

  /*
  * Layer: Anything that can be added to the map. Ex, polygons, circles, markers, popups or tiles etc. You can combine layers in a L.LayerGroup (or FeatureGroup).
  *
  */
  // Function to change color or pop up per country
  const onEachCountry = (country:Feature, layer:Layer&{options:CountryStyle}) => {
    let matchingCountry: Country | null = null;
    if (country && country.properties && country.properties.ADM0_A3 && country.properties.NAME_EN) {
      const countryId = String(country.properties.ADM0_A3);
      const countryName = String(country.properties.NAME_EN);
      matchingCountry = getCountryById(state, countryId);
      if (!matchingCountry) {
        matchingCountry = getCountryByName(state, countryName);
      }
    }
    // Country found in map data and API data.
    if (matchingCountry) {
      if (matchingCountry.state === STATE_DEFAULT) {
        layer.options.fillColor = matchingCountry.defaultStyle.fillColor;
        layer.options.color = matchingCountry.defaultStyle.color;
        layer.options.weight = matchingCountry.defaultStyle.weight;
        layer.options.fillOpacity = matchingCountry.defaultStyle.fillOpacity;
        layer.bindTooltip(getCountryNameByLang(matchingCountry), {
          sticky: true,
        });
      }
      else if (matchingCountry.state === STATE_ACTIVE) {
        layer.options.fillColor = matchingCountry.activeStyle.fillColor;
        layer.options.color = matchingCountry.activeStyle.color;
        layer.options.weight = matchingCountry.activeStyle.weight;
        layer.options.fillOpacity = matchingCountry.activeStyle.fillOpacity;
        layer.bindTooltip(getCountryNameByLang(matchingCountry), {
          sticky: true,
        });
      }
      else {
        layer.options.fillColor = matchingCountry.inactiveStyle.fillColor;
        layer.options.color = matchingCountry.inactiveStyle.color;
        layer.options.weight = matchingCountry.inactiveStyle.weight;
        layer.options.fillOpacity = matchingCountry.inactiveStyle.fillOpacity;
        layer.bindTooltip(getCountryNameByLang(matchingCountry), {
          sticky: true,
        });
      }
      // Set onclick listeners for every country.
      layer.on({
        // Eslint does not understand that matchingCountry cannot be null here.
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        click: (e) => {onSelect(e, matchingCountry, layer)},
        mouseover: onHover,
        mouseout: onUnhover,
      });
    }
    else {
      layer.options.fillColor = countryStyle.fillColor;
        layer.options.color = countryStyle.color;
        layer.options.weight = countryStyle.weight;
        layer.options.fillOpacity = countryStyle.fillOpacity;
    }
  };

  const countryStyle = {
    fillColor: "#fff",
    fillOpacity: 0.7,
    color: "black",
    weight: 1,
  }

  const mapContainerStyle = { 
    backgroundColor:"#b4dcfc", 
    height: "55vh"
  };

  // Store map data into new variable with types defined
  const countries: any = {type: mapData.type, features: mapData.features};

  /*
    This component is listening to drag events
    and updating the map center on drag end.
  */
  const MapEventListener = () => {
    const map = useMapEvents({
      dragend: (e) => {
        setCenter(e.target.getCenter());
      },
      zoomend: (e) => {
        setZoom(e.target.getZoom());
      }
    });
    return null;
  }

  return (
    <div className="map">
      <MapContainer
        attributionControl={false}
        key={changed.toString()}
        style={mapContainerStyle}
        zoom ={zoom}
        center={props.center}
        scrollWheelZoom={false}>
        <MapEventListener/>
        <GeoJSON
          key={props.selectedCountry?.nameEng}
          data={countries}
          onEachFeature={onEachCountry}
        >
        </GeoJSON>
      </MapContainer>
    </div>
  );
}

export default Map;